import { DataService } from './dataService';

// Extend Window interface to include vmtChartData
declare global {
  interface Window {
    vmtChartData?: any[];
  }
}

export interface VMTDataPoint {
  year: number;
  vmt: number;
  source?: string;
  method_note?: string;
}

export interface VMTDataResult {
  years: number[];
  values: number[];
  sources: string[];
  providers: string[];
}

/**
 * VMT Data Service for GHG Inventory
 * This service handles VMT data from the Transportation page (with user customizations)
 * and provides it to the GHG Inventory for emissions calculations
 */

// Global VMT data storage
let globalVMTData: VMTDataPoint[] = [];

/**
 * Set VMT data from Transportation page (called by Transportation page)
 * This includes user customizations and extrapolation
 */
export function setVMTDataFromTransportation(data: VMTDataPoint[]): void {
  globalVMTData = data;
  console.log('🚗 VMT Data Service: Set VMT data from Transportation page:', data.length, 'records');
  console.log('🚗 VMT Data Service: Sample data:', data.slice(0, 3));
  
  // Clear baseline cache when VMT data changes (will be recalculated on next Policy Tool access)
  // Note: We can't clear by location here since we don't have it, but the cache validation will detect the change
  import('./policyBaselineCache').then(({ clearBaselineCache }) => {
    // Clear all baseline caches since VMT data change affects all locations
    // The cache validation will ensure only affected locations are recalculated
    clearBaselineCache();
  }).catch(() => {
    // Ignore errors if policyBaselineCache is not available (e.g., during initial load)
  });
}

/**
 * Get VMT data for GHG Inventory calculations
 * Returns data from Transportation page only - no fallback to Supabase
 * @param location - Location name
 * @param geofips - Optional geofips code
 * @param emfacVersion - Optional EMFAC version ('2021' or '2025') for backcasting data
 */
export async function getVMTDataForGHG(location: string, geofips?: number, emfacVersion: '2021' | '2025' = '2025'): Promise<VMTDataPoint[]> {
  // First, try to get data from Transportation page (globalVMTData)
  if (globalVMTData.length > 0) {
    console.log('✅ VMT Data Service: Using Transportation page data:', globalVMTData.length, 'records');
    return globalVMTData;
  }

  // Second, try to get data from legacy window variable (set by Transportation page)
  if (typeof window !== 'undefined' && window.vmtChartData && window.vmtChartData.length > 0) {
    console.log('✅ VMT Data Service: Using legacy window VMT data:', window.vmtChartData.length, 'records');
    const legacyData = window.vmtChartData.map((d: any) => ({
      year: d.year,
      vmt: d.vmt || 0,
      source: 'transportation_page',
      method_note: 'Legacy window variable from Transportation page'
    }));
    return legacyData;
  }

  // Fallback: Fetch VMT data directly from Supabase using same methods as Transportation page
  console.log('⚠️ VMT Data Service: No cached VMT data found, fetching directly from Supabase...');
  
  try {
    let vmtDbData: any[] = [];
    
    if (geofips) {
      console.log('🔍 VMT Data Service: Fetching by geofips using DataService:', geofips);
      try {
        vmtDbData = await DataService.getVMTData(undefined, ['total'], geofips);
        console.log('🔍 VMT Data Service: DataService returned:', vmtDbData.length, 'records');
      } catch (error) {
        console.warn('DataService.getVMTData failed:', error);
      }
    }
    
    // If no data found by geofips, try by location name
    if (vmtDbData.length === 0) {
      console.log('🔍 VMT Data Service: Fetching by location name using DataService:', location);
      try {
        vmtDbData = await DataService.getVMTData(location, ['total']);
        console.log('🔍 VMT Data Service: DataService by location returned:', vmtDbData.length, 'records');
      } catch (error) {
        console.warn('DataService.getVMTData by location failed:', error);
      }
    }
    
    // If still no data, try the scaling method (like Transportation page does)
    if (vmtDbData.length === 0 && geofips) {
      console.log('🔍 VMT Data Service: Trying DataService.getVMTDataWithScaling:', geofips);
      try {
        vmtDbData = await DataService.getVMTDataWithScaling(geofips);
        console.log('🔍 VMT Data Service: DataService scaling returned:', vmtDbData.length, 'records');
        
        // Aggregate VMT data by year - sum PRIVATE_AUTO and ON_DEMAND_AUTO modes (like Transportation page)
        if (vmtDbData.length > 0) {
          const aggregated: Record<number, number> = {};
          for (const record of vmtDbData) {
            // Only include PRIVATE_AUTO and ON_DEMAND_AUTO modes (light-duty vehicles)
            if (record.mode === 'PRIVATE_AUTO' || record.mode === 'ON_DEMAND_AUTO') {
              const year = record.year;
              aggregated[year] = (aggregated[year] || 0) + (record.vmt || 0);
            }
          }
          
          // Convert back to array format
          vmtDbData = Object.entries(aggregated).map(([year, vmt]) => ({
            year: parseInt(year),
            vmt: vmt,
            mode: 'total', // Mark as aggregated
            source: 'supabase_direct_scaled'
          }));
          
          console.log('🔍 VMT Data Service: Aggregated VMT data by year:', vmtDbData.length, 'years');
        }
      } catch (error) {
        console.warn('DataService.getVMTDataWithScaling failed:', error);
      }
    }
    
    const vmtData = vmtDbData.map(d => ({
      year: d.year,
      vmt: d.vmt || 0,
      source: 'supabase_direct',
      method_note: 'VMT data fetched directly from Supabase (same source as Transportation page)'
    }));
    
    // Update global state with fetched data so it persists for subsequent calls
    if (vmtData.length > 0) {
      console.log('✅ VMT Data Service: Setting fetched VMT data in global state');
      globalVMTData = vmtData;
      // Also set legacy window variable
      if (typeof window !== 'undefined') {
        window.vmtChartData = vmtData;
      }
    }
    
    console.log('✅ VMT Data Service: Fetched from Supabase:', vmtData.length, 'records');
    return vmtData;
  } catch (error) {
    console.warn('Error fetching VMT data from Supabase:', error);
    return [];
  }
}

/**
 * Get VMT data formatted for GHG calculations
 * Returns data in the format expected by GHG Inventory
 */
export async function getVMTDataForGHGCalculations(location: string): Promise<VMTDataResult> {
  const vmtData = await getVMTDataForGHG(location);
  
  if (vmtData.length === 0) {
    return {
      years: [],
      values: [],
      sources: [],
      providers: []
    };
  }

  // Sort by year and extract arrays
  const sortedData = vmtData.sort((a, b) => a.year - b.year);
  
  return {
    years: sortedData.map(d => d.year),
    values: sortedData.map(d => d.vmt || 0),
    sources: sortedData.map(d => d.source || 'unknown'),
    providers: sortedData.map(d => d.method_note || 'Unknown source')
  };
}

/**
 * Clear VMT data (useful for testing or when location changes)
 */
export function clearVMTData(): void {
  globalVMTData = [];
  console.log('🚗 VMT Data Service: Cleared VMT data');
}

/**
 * Get current VMT data status
 */
export function getVMTDataStatus(): { hasData: boolean; recordCount: number; years: number[] } {
  return {
    hasData: globalVMTData.length > 0,
    recordCount: globalVMTData.length,
    years: globalVMTData.map(d => d.year).sort()
  };
}

/**
 * Check if VMT data is available from Transportation page
 * Returns true if VMT data has been loaded from Transportation page
 */
export function isVMTDataAvailable(): boolean {
  // Check global VMT data first (set by Transportation page)
  if (globalVMTData.length > 0) {
    return true;
  }
  
  // Check legacy window variable (set by Transportation page)
  if (typeof window !== 'undefined' && window.vmtChartData && window.vmtChartData.length > 0) {
    return true;
  }
  
  return false;
}

